import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Stack,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Avatar,
  Badge,
  Tooltip
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HomeIcon from '@mui/icons-material/Home';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isTokenExpired } from '../utils/authUtils';
import secureStorage from '../utils/secureStorage';
import { RoleNames } from '../constants/roles';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import CookieIcon from '@mui/icons-material/Cookie';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BarChartIcon from '@mui/icons-material/BarChart';
import ArticleIcon from '@mui/icons-material/Article';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessIcon from '@mui/icons-material/Business';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import InboxIcon from '@mui/icons-material/InboxOutlined';
import PeopleIcon from '@mui/icons-material/People';
import SellIcon from '@mui/icons-material/SellOutlined';
import AddHomeWorkIcon from '@mui/icons-material/AddHomeWorkOutlined';
import ChatIcon from '@mui/icons-material/ChatBubbleOutline';
import NotificationBell from './NotificationBell';
import { canUseAgentArea } from './auth/AgentRoute';
import { useAccountPhoto } from '../utils/accountPhoto';
import { NEW_PROPERTY_PATH, SELLER_NEW_PROPERTY_PATH } from './agent/workspace/paths';
import { openCookieSettings } from '../utils/cookieConsent';

/** Resolved per user: a visitor signs up first, an owner goes to the form. */
const SELL_PATH = '__sell__';

/**
 * One navigation model for both the desktop bar and the phone drawer, so a link
 * can never exist in one and not the other. The primary four are what people
 * come here to do and appear inline above `md`; the rest live in the drawer and
 * the footer. Labels are sentence case (design.md §5.8).
 */
// Items render as real links (<a href>) so crawlers can follow them, except
// those marked `crawlLink: false`.
const PRIMARY_NAV = [
  { text: 'Find homes', icon: <HomeWorkIcon />, path: '/properties' },
  { text: 'Sell my home', icon: <SellIcon />, path: SELL_PATH },
  { text: 'Market', icon: <TrendingUpIcon />, path: '/market' },
  { text: 'Blog', icon: <ArticleIcon />, path: '/blog' },
  // { text: 'Chat', icon: <ChatIcon />, path: '/chat' },
];

// An agent adds a property to list, not their own home to sell: the same add-property page at
// their own URL, which opens the new property in their listing workspace.
const AGENT_SELL_ITEM = { text: 'New listing', icon: <AddHomeWorkIcon />, path: NEW_PROPERTY_PATH };

const SECONDARY_NAV = [
  // { text: 'Home', icon: <HomeIcon />, path: '/' },
  { text: 'About', icon: <InfoIcon />, path: '/about' },
  { text: 'FAQ', icon: <HelpIcon />, path: '/faq' },
  { text: 'Contact', icon: <ContactSupportIcon />, path: '/contact' },
  { text: 'Cookie settings', icon: <CookieIcon />, action: 'cookie-settings' },
];

const Header = () => {
  const theme = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const navigate = useNavigate();
  const { state, logout } = useAuth();
  const location = useLocation();
  const isLoggedIn = Boolean(state?.isAuthenticated && state?.user);

  // Selling starts at sign-up for a visitor and at the add-property page for an owner,
  // so both paths reach the same first screen.
  const sellDestination = isLoggedIn ? SELLER_NEW_PROPERTY_PATH : '/signup?intent=sell';
  const resolvePath = useCallback(
    (path) => (path === SELL_PATH ? sellDestination : path),
    [sellDestination],
  );

  // Signed-in people get their own two destinations beside the public four.
  const accountNav = useMemo(
    () => (isLoggedIn
      ? [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        // { text: 'My properties', icon: <HomeWorkIcon />, path: '/my-properties' },
      ]
      : []),
    [isLoggedIn],
  );

  const agentUser = canUseAgentArea(state?.user);
  // An agent's account picture is their photo from Settings (their upload, else the board's).
  const agentPhoto = useAccountPhoto(isLoggedIn && agentUser ? state.user : null);
  const primaryNav = useMemo(
    () => (agentUser ? PRIMARY_NAV.map((item) => (item.path === SELL_PATH ? AGENT_SELL_ITEM : item)) : PRIMARY_NAV),
    [agentUser],
  );
  const inlineNav = useMemo(() => [...primaryNav, ...accountNav], [primaryNav, accountNav]);
  const drawerNav = useMemo(
    () => [...inlineNav, ...SECONDARY_NAV],
    [inlineNav],
  );

  const handleNavClick = useCallback((item) => {
    if (item.action === 'cookie-settings') {
      openCookieSettings();
    } else {
      navigate(resolvePath(item.path), item.state ? { state: item.state } : undefined);
    }
  }, [navigate, resolvePath]);

  // A path item is a router link; an action (cookie settings) or a
  // `crawlLink: false` item stays a button.
  const navTargetProps = useCallback((item, afterClick) => (
    item.path && item.crawlLink !== false
      ? { component: RouterLink, to: resolvePath(item.path), state: item.state, onClick: afterClick }
      : { onClick: () => { handleNavClick(item); if (afterClick) afterClick(); } }
  ), [handleNavClick, resolvePath]);

  useEffect(() => {
    const checkToken = async () => {
      const token = secureStorage.getToken();
      if (state?.isAuthenticated && token && token !== 'cookie-auth' && isTokenExpired(token)) {
        await logout();
        navigate('/login');
      }
    };
    checkToken();
  }, [state?.isAuthenticated, logout, navigate]);

  useEffect(() => {
    // Reset image error state when current user/avatar changes.
    setAvatarLoadFailed(false);
  }, [
    state?.user?.id,
    state?.user?.avatar_url,
    state?.user?.avatarUrl,
    state?.user?.picture,
    state?.user?.profile_image,
    state?.user?.profileImage,
    agentPhoto,
  ]);

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Update avatar display
  const getInitials = () => {
    if (!state.user) return '';
    return `${state.user.first_name?.[0]?.toUpperCase() || ''}${state.user.last_name?.[0]?.toUpperCase() || ''}`;
  };

  const getAvatarUrl = () => {
    const avatarUrl =
      state?.user?.avatar_url ||
      state?.user?.avatarUrl ||
      state?.user?.picture ||
      state?.user?.profile_image ||
      state?.user?.profileImage ||
      null;

    if (typeof avatarUrl !== 'string') {
      return null;
    }
    const normalized = avatarUrl.trim();
    return normalized || null;
  };

  const avatarUrl = avatarLoadFailed ? null : agentPhoto || getAvatarUrl();

  return (
    <>
      <AppBar 
        position="fixed"
        elevation={0} 
        data-testid="auth-state"
        data-authenticated={state.isAuthenticated}
        data-user={state.user?.email || 'none'}
        sx={{ 
          // background: theme.modern.backgrounds.gradient,
          borderBottom: `1px solid ${theme.modern.colors.modern.slate[300]}`,
          // backdropFilter: 'blur(10px)',
          backgroundColor: alpha(theme.palette.common.white, 0.99),
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: theme.shape.borderRadius,
          borderBottomRightRadius: theme.shape.borderRadius,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
            sx={{ mr: 2, color: theme.palette.text.primary, display: { xs: 'inline-flex', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography 
            variant="h5" 
            sx={{ 
              ...theme.modern.utils.gradientText('primary'),
              fontWeight: 400,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              textDecoration: 'none',
              mr: 'auto' // Push user menu to the right
            }}
            component={RouterLink}
            to="/"
          >
            {/* <HomeIcon sx={{ color: theme.modern.colors.modern.mainColor[500] }} /> */}
            KeyTa
          </Typography>
          
          {/* Inline navigation on desktop; the drawer carries the same links on phones. */}
          <Stack
            component="nav"
            aria-label="Main"
            direction="row"
            spacing={0.5}
            sx={{ display: { xs: 'none', md: 'flex' }, mr: 2 }}
          >
            {inlineNav.map((item) => {
              const target = resolvePath(item.path);
              const active = location.pathname === target
                || (target.length > 1 && location.pathname.startsWith(`${target}/`));
              return (
                <Button
                  key={item.text}
                  {...navTargetProps(item)}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    px: 1.25,
                    color: active ? 'primary.main' : 'text.secondary',
                    fontWeight: active ? 700 : 600,
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                  }}
                >
                  {item.text}
                </Button>
              );
            })}
          </Stack>

          <Stack direction="row" spacing={1}>
            {isLoggedIn ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <NotificationBell />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      padding: '6px 4px',
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.text.primary, 0.04)
                      }
                    }}
                    onClick={handleUserMenuClick}
                  >
                    <Avatar
                      src={avatarUrl || undefined}
                      alt={`${state.user?.first_name || ''} ${state.user?.last_name || ''}`.trim() || 'User avatar'}
                      onError={() => setAvatarLoadFailed(true)}
                      sx={{
                        width: 32,
                        height: 32,
                        background: avatarUrl ? 'transparent' : theme.modern.colors.gradient.primary,
                        fontSize: '0.875rem'
                      }}
                    >
                      {getInitials()}
                    </Avatar>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          lineHeight: 1.2
                        }}
                      >
                        {/* {state.user?.first_name} {state.user?.last_name} */}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem'
                        }}
                      >
                        {/* {state.user?.primary_role?.name || (state.user?.roles?.length > 0 ? state.user.roles[0].name : 'User')} */}
                        {(() => {
                        const roleName = state.user?.primary_role?.display_name || (state.user?.roles?.length > 0 ? state.user.roles[0].display_name : '');
                        return roleName.toLowerCase() !== 'client' && (
                          <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem'
                          }}
                          >
                          {roleName}
                          </Typography>
                        );
                        })()}
                      </Typography>
                    </Box>
                  </Box>

                  <Menu
                    id="user-menu"
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    PaperProps={{
                      sx: {
                        mt: 1.5,
                        minWidth: 200,
                        borderRadius: 2,
                        boxShadow: theme.shadows[8]
                      }
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {state.user?.first_name} {state.user?.last_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {state.user?.email}
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <MenuItem 
                      onClick={() => {
                        handleUserMenuClose();
                        navigate('/profile');
                      }}
                      sx={{ 
                        py: 1,
                        gap: 1.5
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                      <Typography variant="body2">Profile</Typography>
                    </MenuItem>
                    
                    <MenuItem 
                      onClick={() => {
                        handleUserMenuClose();
                        navigate('/dashboard');
                      }}
                      sx={{ 
                        py: 1,
                        gap: 1.5
                      }}
                    >
                      <DashboardIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                      <Typography variant="body2">Dashboard</Typography>
                    </MenuItem>

                    {/* Agent workspace pages: agents only */}
                    {state.user?.roles?.some(role => role.name === RoleNames.AGENT) && [
                      <MenuItem key="agent-properties" onClick={() => { handleUserMenuClose(); navigate('/agent/properties'); }} sx={{ py: 1, gap: 1.5 }}>
                        <HomeWorkIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                        <Typography variant="body2">My listings</Typography>
                      </MenuItem>,
                      <MenuItem key="agent-approvals" onClick={() => { handleUserMenuClose(); navigate('/agent/approvals'); }} sx={{ py: 1, gap: 1.5 }}>
                        <FactCheckIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                        <Typography variant="body2">Approvals</Typography>
                      </MenuItem>,
                      <MenuItem key="agent-tasks" onClick={() => { handleUserMenuClose(); navigate('/agent/tasks'); }} sx={{ py: 1, gap: 1.5 }}>
                        <TaskAltIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                        <Typography variant="body2">Tasks</Typography>
                      </MenuItem>,
                      <MenuItem key="agent-inquiries" onClick={() => { handleUserMenuClose(); navigate('/agent/inquiries'); }} sx={{ py: 1, gap: 1.5 }}>
                        <InboxIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                        <Typography variant="body2">Inquiries</Typography>
                      </MenuItem>,
                    ]}

                    {/* Brokerage: members and brokerage admins only */}
                    {(state.user?.brokerage_id ||
                      state.user?.managed_brokerages?.length > 0 ||
                      state.user?.roles?.some(role => role.name === RoleNames.BROKERAGE_ADMIN)) && (
                      <MenuItem
                        onClick={() => {
                          handleUserMenuClose();
                          navigate('/agent/brokerage');
                        }}
                        sx={{
                          py: 1,
                          gap: 1.5
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                        <Typography variant="body2">Brokerage</Typography>
                      </MenuItem>
                    )}

                    {/* Super Admin Menu Items - Show for super admin users only */}
                    {(state.user?.roles?.some(role => role.name === RoleNames.SUPER_ADMIN) || 
                      state.user?.primary_role?.name === RoleNames.SUPER_ADMIN) && [
                        <Divider key="super-admin-divider-1" />,
                        <MenuItem 
                          key="rbac-management"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/rbac-management');
                          }}
                          sx={{ 
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <SecurityIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                          <Typography variant="body2">RBAC Management</Typography>
                        </MenuItem>,

                        <MenuItem
                          key="users-management"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/users-management');
                          }}
                          sx={{
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <PeopleIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                          <Typography variant="body2">Users And Visitors</Typography>
                        </MenuItem>,

                        <MenuItem
                          key="users-properties"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/users-properties');
                          }}
                          sx={{
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <HomeWorkIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                          <Typography variant="body2">User Properties</Typography>
                        </MenuItem>,

                        <Divider key="super-admin-divider-2" />,
                        <MenuItem 
                          key="prompts"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/prompts');
                          }}
                          sx={{ 
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <BarChartIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                          <Typography variant="body2">Prompts</Typography>
                        </MenuItem>,
                        <MenuItem 
                          key="history"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/history');
                          }}
                          sx={{ 
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <ArticleIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                          <Typography variant="body2">Conversations History</Typography>
                        </MenuItem>,                      
                        <Divider key="super-admin-divider-3" />,
                        <MenuItem 
                          key="metrics"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/metrics');
                          }}
                          sx={{ 
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <BarChartIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                          <Typography variant="body2">System Metrics</Typography>
                        </MenuItem>,
                        <MenuItem 
                          key="logs"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/logs');
                          }}
                          sx={{ 
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <ArticleIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                          <Typography variant="body2">System Logs</Typography>
                        </MenuItem>,
                        <Divider key="super-admin-divider-4" />,
                        <MenuItem 
                          key="admin-settings"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/settings');
                          }}
                          sx={{ 
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <SettingsIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                          <Typography variant="body2">System Settings</Typography>
                        </MenuItem>
                      ]}

                    <Divider />

                    <MenuItem 
                      onClick={() => {
                        handleUserMenuClose();
                        handleLogout();
                      }}
                      sx={{ 
                        py: 1,
                        gap: 1.5,
                        color: 'error.main'
                      }}
                    >
                      <LogoutIcon sx={{ fontSize: 20 }} />
                      <Typography variant="body2">Logout</Typography>
                    </MenuItem>
                  </Menu>
                </Box>
              </>
            ) : (
              <>
                <Button 
                  variant="text" 
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) }
                  }}
                  onClick={() => navigate('/login')}
                >
                  Log in
                </Button>
                <Button 
                  variant="gradient" 
                  onClick={() => navigate('/signup')}
                >
                  Sign up
                </Button>
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        // Keeps the drawer's links in the page while it is closed: Google
        // renders the phone layout, where they are the only header links.
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ width: 280, pt: 2, pb: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, mb: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                ...theme.modern.typography.modernHeading,
                fontWeight: 700 
              }}
            >
              Menu
            </Typography>
            <IconButton onClick={() => setMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <List sx={{ flexGrow: 1 }}>
            {drawerNav.map((item) => (
              <ListItemButton
                key={item.text}
                {...navTargetProps(item, () => setMenuOpen(false))}
              >
                <ListItemIcon sx={{ color: theme.modern.colors.modern.slate[500], minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
          </List>

          {!isLoggedIn ? (
            <>
              {/* <Divider /> */}
              <Box sx={{ p: 2 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 2, 
                    ...theme.modern.typography.modernSubtext 
                  }}
                >
                  {/* Account */}
                </Typography>
                <Button
                  fullWidth
                  variant="gradient"
                  sx={{ 
                    mb: 1.5,
                    // borderColor: 'rgba(0, 0, 0, 0.1)',
                    // color: '#1a2b4c',
                  }}
                  onClick={() => {
                    navigate('/login');
                    setMenuOpen(false);
                  }}
                >
                  Log in
                </Button>
                <Button
                  fullWidth
                  variant="gradient"
                  onClick={() => {
                    navigate('/signup');
                    setMenuOpen(false);
                  }}
                >
                  Sign up
                </Button>
              </Box>
            </>
          ) : (
            <>
              {/* <Divider /> */}
              <Box sx={{ px: 3, pb: 0}}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 0, 
                    ...theme.modern.typography.modernSubtext 
                  }}
                >
                </Typography>
                 <Button
                    variant="gradient"
                    fullWidth
                    onClick={() => navigate('/profile')}
                    sx={{
                      my: 0,
                    }}
                  >
                    My Profile
                  </Button>
                  <Button
                    variant="dark"
                    fullWidth
                    color="error"
                    onClick={handleLogout}
                    sx={{ my: 1 }}
                  >
                    Logout
                  </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Header;